import React, {createContext, useContext, useState, useCallback, ReactNode} from 'react';

/**
 * Context interface for tracking component viewport visibility
 */
interface PositionMarkerContextValue {
    /** Whether the tracked component is currently in view */
    isComponentInView: boolean;
    /** Callback to update the component's visibility state */
    setComponentOutOfView: (isInView: boolean) => void;
}

/**
 * Context for managing position marker state
 */
const PositionMarkerContext = createContext<PositionMarkerContextValue | undefined>(undefined);

/**
 * Props for the PositionMarkerProvider component
 */
interface PositionMarkerProviderProps {
    /** Child components to render */
    children: ReactNode;
}

/**
 * Provider component for position marker context
 * Manages the visibility state of a single tracked component
 */
export function PositionMarkerProvider({children}: PositionMarkerProviderProps) {
    const [isComponentInView, setIsComponentInView] = useState(true);

    const setComponentOutOfView = useCallback((isInView: boolean) => {
        setIsComponentInView(isInView);
    }, []);

    const value: PositionMarkerContextValue = {
        isComponentInView,
        setComponentOutOfView,
    };

    return (
        <PositionMarkerContext.Provider value={value}>{children}</PositionMarkerContext.Provider>
    );
}

/**
 * Hook to access position marker context
 * @throws Error if used outside of PositionMarkerProvider
 */
export function usePositionMarker(): PositionMarkerContextValue {
    const context = useContext(PositionMarkerContext);

    if (context === undefined) {
        throw new Error('usePositionMarker must be used within a PositionMarkerProvider');
    }

    return context;
}
